import { ActionReference, GameObjectReference, GameState } from "@shared/types";
import { LitElement, TemplateResult, css, html, nothing } from "lit";
import { customElement } from "lit/decorators.js";
import { getState, performAction } from "../services/routeService";
import "./MLMiniGame.js";

@customElement("game-canvas")
export class GameCanvas extends LitElement {
    public static styles = css`
        .game {
            height: 100%;
            display: grid;
            grid-template-columns: 1fr;
            grid-template-rows: auto auto 1fr auto;
            grid-column-gap: 0px;
            grid-row-gap: 0px;
        }

        .title {
            text-align: center;
            margin-top: 10px;
        }

        .header {
            display: flex;
            flex-direction: column;
            align-items: center;
            flex-grow: 1;
            position: relative;
            margin-top: 10px;
        }

        .header img {
            width: 100%;
            height: auto;
            image-rendering: pixelated;
        }

        .header img:nth-child(n + 2) {
            position: absolute;
        }

        .content {
            flex-grow: 1;
            overflow: auto;
            margin-top: 10px;
            padding: 0 10px;
        }

        .content p {
            margin: 0 0 10px 0;
        }

        .content p:last-of-type {
            margin: 0;
        }

        .inventory-container {
            width: 1%;
            background-color: #7f6ed7;
            border: 1px solid #332c57;
            padding: 5px 10px;
            margin: 0 0 10px 10px;
            text-transform: uppercase;
            cursor: pointer;
            display: flex;
            user-select: none;
            color: white;
            justify-self: flex-end;
            position: fixed;
            right: 0;
            flex-direction: column;
            height: 100%;
        }

        .inventory-container:hover {
            width: 15%;
        }

        .inventory-header {
            writing-mode: vertical-lr;
            text-align: center;
            transform: rotate(180deg);
        }

        .inventory {
            display: flex;
            flex-direction: column;
            text-align: center;
            cursor: pointer;
            visibility: hidden;
            top: 0%;
        }

        .inventory-container:hover .inventory {
            visibility: visible;
        }

        .inventory-container:hover .inventory-header {
            writing-mode: unset;
            transform: none;
            font-size: 2rem;
            margin: 2rem;
            background-color: black;
        }

        #inventory-item {
            color: black;
        }

        #inventory-item:hover {
            background-color: white;
            color: black;
            cursor: pointer;
        }

        .footer {
            border-radius: 10px 10px 0 0;
            background-color: #52478b;
            border: 1px solid #332c57;
            margin-top: 10px;
            display: flex;
            height: 105px;
        }

        .footer .buttons {
            display: flex;
            flex-direction: column;
            overflow: auto;
            padding: 10px 10px 0 10px;
        }

        .footer .button {
            background-color: #7f6ed7;
            border: 1px solid #332c57;
            padding: 5px 10px;
            margin: 0 0 10px 10px;
            text-transform: uppercase;
            cursor: pointer;
            display: inline-block;
            user-select: none;
        }

        .footer .button.active,
        .footer .button:hover {
            background-color: #332c57;
        }
    `;

    private roomTitle?: string;
    private roomImages?: string[];
    private contentText?: string[];
    private actionButtons?: ActionReference[];
    private gameObjectButtons?: GameObjectReference[];
    private directions?: GameObjectReference[];
    private inventory?: GameObjectReference[];
    private isMLMiniGamePassed = true;

    private selectedActionButton?: ActionReference;
    private selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    public connectedCallback(): void {
        super.connectedCallback();

        void this.refreshState();
    }

    private async refreshState(): Promise<void> {
        const state: GameState = await getState();

        this.updateState(state);
    }

    private updateState(state: GameState): void {
        //Reset the component
        this.roomTitle = state.roomTitle;
        this.roomImages = state.roomImages;
        this.contentText = state.text;
        this.actionButtons = state.actions;
        this.gameObjectButtons = state.objects;
        this.directions = state.directions;
        this.inventory = state.inventory;

        if (state.resetButtons) {
            this.selectedActionButton = undefined;
            this.selectedGameObjectButtons.clear();
        }

        this.requestUpdate();
    }

    private async handleClickAction(button: ActionReference): Promise<void> {
        if (button.needsObject) {
            this.selectedActionButton = button;
            this.selectedGameObjectButtons.clear();

            this.requestUpdate();
        } else {
            const state: any = await performAction(button.alias);

            if (state === undefined) {
                return;
            }

            this.updateState(state);
        }
    }

    private async handleClickObject(button: GameObjectReference): Promise<void> {
        if (!this.selectedActionButton) {
            return;
        }

        this.selectedGameObjectButtons.add(button);

        let state: GameState | undefined;

        if (this.selectedActionButton.alias === "read" && this.isMLMiniGamePassed === true) {
            this.isMLMiniGamePassed = false;
        } else {
            state = await performAction(
                this.selectedActionButton.alias,
                [...this.selectedGameObjectButtons].map((e) => e.alias)
            );
        }

        if (this.selectedGameObjectButtons.size >= 2) {
            this.selectedActionButton = undefined;
            this.selectedGameObjectButtons.clear();
        }

        this.requestUpdate();

        if (state === undefined) {
            return;
        }

        this.updateState(state);
    }

    protected render(): TemplateResult {
        return html`
            <div class="game">
                ${this.renderTitle()} ${this.renderHeader()} ${this.renderContent()}
                ${this.renderMLMiniGame()} ${this.renderInventory()} ${this.renderFooter()}
            </div>
        `;
    }

    private renderTitle(): TemplateResult {
        if (this.roomTitle) {
            return html`<div class="title">${this.roomTitle}</div>`;
        }

        return html`${nothing}`;
    }

    private renderHeader(): TemplateResult {
        if (this.roomImages && this.roomImages.length > 0) {
            return html`
                <div class="header">
                    ${this.roomImages?.map((url) => html`<img src="/assets/img/rooms/${url}.png" />`)}
                </div>
            `;
        }

        return html`${nothing}`;
    }

    private renderContent(): TemplateResult {
        return html` <div class="content">${this.contentText?.map((text) => html`<p>${text}</p>`)}</div> `;
    }

    private renderMLMiniGame(): TemplateResult {
        return html`${this.isMLMiniGamePassed === false ? html`<ml-mini-game></ml-mini-game>` : html``}`;
    }

    private showCorrectButtons(): GameObjectReference[] | undefined {
        switch (this.selectedActionButton?.alias) {
            case "go-action":
                return this.directions;

            default:
                return this.gameObjectButtons;
        }
    }

    private renderInventory(): TemplateResult | undefined {
        if (!(this.roomTitle === "Luca Star")) {
            const gameObjectAliases: Set<string> = new Set(
                [...this.selectedGameObjectButtons].map((e) => e.alias)
            );
            return html` <div class="inventory-container">
                <div class="inventory-header">Inventory</div>
                <div class="inventory">
                    ${this.inventory?.map(
                        (item) => html`<a
                            id="inventory-item"
                            class="button ${gameObjectAliases.has(item.alias) ? "active" : ""}"
                            @click=${(): void => void this.handleClickObject(item)}
                            >${item.name}</a
                        >`
                    )}
                </div>
            </div>`;
        }

        return undefined;
    }

    private renderFooter(): TemplateResult {
        //NOTE: Convert Set of GameObjectReference to Set of aliases
        const gameObjectAliases: Set<string> = new Set(
            [...this.selectedGameObjectButtons].map((e) => e.alias)
        );

        return html`
            <div class="footer">
                <div class="buttons">
                    <div>
                        ${this.actionButtons?.map(
                            (button) => html`<a
                                class="button ${this.selectedActionButton?.alias === button.alias
                                    ? "active"
                                    : ""}"
                                @click=${(): void => void this.handleClickAction(button)}
                                >${button.label}</a
                            >`
                        )}
                    </div>
                    <div>
                        ${this.selectedActionButton
                            ? this.showCorrectButtons()?.map(
                                  (button) => html`<a
                                      class="button ${gameObjectAliases.has(button.alias) ? "active" : ""}"
                                      @click=${(): void => void this.handleClickObject(button)}
                                      >${button.name}</a
                                  >`
                              )
                            : nothing}
                    </div>
                </div>
            </div>
        `;
    }
}
